import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Copy,
  FileText,
  Film,
  Folder,
  FolderPlus,
  Images,
  Loader2,
  Pencil,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Card, CardContent } from '../../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { cn } from '../../../../lib/utils';
import {
  cmsFolderApi,
  cmsMediaApi,
  type AiDisclosure,
  type CmsMediaFolder,
  type CmsMediaItem,
} from '../../../../lib/api/cms';
import {
  AiDisclosureBadge,
  AiDisclosureDialog,
  AiDisclosureSelect,
} from '../../../../components/cms/AiDisclosure';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];
// Das Backend nimmt PDFs an (Flyer, Anmeldeformulare); die Oberfläche lehnte
// sie ab (T-0116).
const DOCUMENT_TYPES = ['application/pdf'];
const UPLOAD_TYPES = [...IMAGE_TYPES, ...VIDEO_TYPES, ...DOCUMENT_TYPES];
const istPdf = (item: { content_type?: string | null }) => item.content_type === 'application/pdf';

const PROCESSING_LABELS: Record<string, string> = {
  pending: 'Wartet…',
  processing: 'Wird verarbeitet…',
  ready: 'Bereit',
  failed: 'Fehlgeschlagen',
};

type Selection = 'all' | 'root' | number;

const CmsMediaLibrary: React.FC = () => {
  const [items, setItems] = useState<CmsMediaItem[]>([]);
  const [folders, setFolders] = useState<CmsMediaFolder[]>([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState<Selection>('all');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [previewItem, setPreviewItem] = useState<CmsMediaItem | null>(null);
  // Frisch hochgeladenes Medium, dessen KI-Kennzeichnung noch offen ist.
  const [pendingDisclosure, setPendingDisclosure] = useState<CmsMediaItem | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const folderParam = selected === 'all' ? undefined : selected === 'root' ? 0 : selected;
  const uploadFolderId = typeof selected === 'number' ? selected : 0;

  const loadFolders = useCallback(async () => {
    try {
      const res = await cmsFolderApi.list();
      setFolders(res.items);
      setUnfiledCount(res.unfiled_count);
      setTotalCount(res.total_count);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ordner konnten nicht geladen werden');
    }
  }, []);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cmsMediaApi.list({ visibility: 'public', q: search || undefined, folder_id: folderParam });
      setItems(res.items);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Medien konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [search, folderParam]);

  useEffect(() => { void loadFolders(); }, [loadFolders]);
  useEffect(() => { void loadMedia(); }, [loadMedia]);

  // Poll transcoding status while any video is still being processed.
  useEffect(() => {
    const pending = items.filter(
      (i) => i.media_kind === 'video' && (i.processing_status === 'pending' || i.processing_status === 'processing'),
    );
    if (pending.length === 0) return;
    const timer = setInterval(async () => {
      const updates = await Promise.all(
        pending.map((i) => cmsMediaApi.processingStatus(i.id).catch(() => null)),
      );
      setItems((prev) =>
        prev.map((it) => {
          const u = updates.find((x) => x && x.id === it.id);
          return u
            ? {
                ...it,
                processing_status: u.processing_status,
                hls_playlist_url: u.hls_playlist_url,
                thumbnail_url: u.thumbnail_url,
                duration_seconds: u.duration_seconds,
              }
            : it;
        }),
      );
    }, 6000);
    return () => clearInterval(timer);
  }, [items]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!UPLOAD_TYPES.includes(file.type)) {
      toast.error('Nur Bilder (PNG, JPEG, WebP), Videos (MP4, MOV, WebM) oder PDFs werden unterstützt');
      return;
    }
    const isVideo = VIDEO_TYPES.includes(file.type);
    const isPdf = DOCUMENT_TYPES.includes(file.type);
    setUploading(true);
    try {
      const uploaded = await cmsMediaApi.upload(file, { title: file.name, folderId: uploadFolderId });
      toast.success(
        isVideo ? 'Video hochgeladen – Verarbeitung läuft' : isPdf ? 'PDF hochgeladen' : 'Bild hochgeladen',
      );
      // Direkt nachfragen: beim Upload weiß man noch, woher die Datei stammt.
      // Die KI-Kennzeichnung gilt Bildern und Videos, nicht Dokumenten.
      if (!isPdf) setPendingDisclosure(uploaded);
      await Promise.all([loadMedia(), loadFolders()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // URL to reference in content: HLS playlist for ready videos, else the file.
  const assetUrl = (item: CmsMediaItem): string | null =>
    item.media_kind === 'video' && item.processing_status === 'ready' && item.hls_playlist_url
      ? item.hls_playlist_url
      : item.public_url;

  const copyUrl = async (url: string | null) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL kopiert');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const remove = async (item: CmsMediaItem) => {
    if (!window.confirm(`„${item.title || item.original_filename}" wirklich löschen?`)) return;
    setDeletingId(item.id);
    try {
      await cmsMediaApi.remove(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      void loadFolders();
      toast.success('Medium gelöscht');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    } finally {
      setDeletingId(null);
    }
  };

  const setDisclosure = async (item: CmsMediaItem, value: AiDisclosure) => {
    const previous = item.ai_disclosure;
    // Optimistisch: die Auswahl soll sofort stehen, sonst springt das Select zurück.
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ai_disclosure: value } : i)));
    try {
      await cmsMediaApi.setAiDisclosure(item.id, value);
    } catch (e) {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ai_disclosure: previous } : i)));
      toast.error(e instanceof Error ? e.message : 'KI-Kennzeichnung konnte nicht gesetzt werden');
    }
  };

  const resolvePendingDisclosure = async (value: AiDisclosure) => {
    const item = pendingDisclosure;
    setPendingDisclosure(null);
    if (!item || value === 'none') return;
    try {
      await cmsMediaApi.setAiDisclosure(item.id, value);
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ai_disclosure: value } : i)));
      toast.success('Als KI-Inhalt gekennzeichnet');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'KI-Kennzeichnung konnte nicht gesetzt werden');
    }
  };

  const moveTo = async (item: CmsMediaItem, folderId: number) => {
    try {
      await cmsMediaApi.move(item.id, folderId);
      // If we're viewing a specific folder, the item may have left it.
      if (folderParam !== undefined && folderId !== folderParam) {
        setItems((prev) => prev.filter((i) => i.id !== item.id));
      } else {
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, folder_id: folderId || null } : i)));
      }
      void loadFolders();
      toast.success('Verschoben');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Verschieben fehlgeschlagen');
    }
  };

  const createFolder = async () => {
    const name = window.prompt('Name des neuen Ordners:')?.trim();
    if (!name) return;
    try {
      const folder = await cmsFolderApi.create(name);
      await loadFolders();
      setSelected(folder.id);
      toast.success('Ordner erstellt');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ordner konnte nicht erstellt werden');
    }
  };

  const renameFolder = async (folder: CmsMediaFolder) => {
    const name = window.prompt('Ordner umbenennen:', folder.name)?.trim();
    if (!name || name === folder.name) return;
    try {
      await cmsFolderApi.rename(folder.id, name);
      await loadFolders();
      toast.success('Ordner umbenannt');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Umbenennen fehlgeschlagen');
    }
  };

  const deleteFolder = async (folder: CmsMediaFolder) => {
    if (!window.confirm(`Ordner „${folder.name}" löschen? Enthaltene Medien werden nach „Ohne Ordner" verschoben.`)) return;
    try {
      await cmsFolderApi.remove(folder.id);
      if (selected === folder.id) setSelected('all');
      await Promise.all([loadFolders(), loadMedia()]);
      toast.success('Ordner gelöscht');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Löschen fehlgeschlagen');
    }
  };

  const NavRow: React.FC<{
    active: boolean;
    icon: React.ReactNode;
    label: string;
    count?: number;
    onClick: () => void;
    children?: React.ReactNode;
  }> = ({ active, icon, label, count, onClick, children }) => (
    <div
      className={cn(
        'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer',
        active ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
      )}
      onClick={onClick}
    >
      <span className="shrink-0">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && <span className="text-xs text-muted-foreground">{count}</span>}
      {children}
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Medienbibliothek</h1>
          <p className="text-sm text-muted-foreground">Bilder und Videos hochladen, in Ordnern organisieren und verwalten. Videos werden automatisch für Web-Streaming (HLS) aufbereitet.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            KI-generierte Medien, die echt wirken könnten, müssen gekennzeichnet werden
            (Art. 50 KI-VO). Die Website blendet dann bei jedem Vorkommen ein Badge ein.
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={UPLOAD_TYPES.join(',')}
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <Button type="button" loading={uploading} onClick={() => fileInputRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" /> Hochladen
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        {/* Folder sidebar */}
        <div className="space-y-1">
          <NavRow active={selected === 'all'} icon={<Images className="h-4 w-4" />} label="Alle Medien" count={totalCount} onClick={() => setSelected('all')} />
          <NavRow active={selected === 'root'} icon={<Folder className="h-4 w-4" />} label="Ohne Ordner" count={unfiledCount} onClick={() => setSelected('root')} />
          <div className="my-2 border-t" />
          {folders.map((f) => (
            <NavRow
              key={f.id}
              active={selected === f.id}
              icon={<Folder className="h-4 w-4" />}
              label={f.name}
              count={f.media_count}
              onClick={() => setSelected(f.id)}
            >
              <span className="hidden gap-1 group-hover:flex">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={(e) => { e.stopPropagation(); void renameFolder(f); }}
                  aria-label="Umbenennen"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={(e) => { e.stopPropagation(); void deleteFolder(f); }}
                  aria-label="Ordner löschen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            </NavRow>
          ))}
          <Button type="button" variant="ghost" size="sm" className="mt-1 w-full justify-start" onClick={createFolder}>
            <FolderPlus className="mr-2 h-4 w-4" /> Neuer Ordner
          </Button>
        </div>

        {/* Media grid */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Keine Medien in dieser Ansicht. Lade ein Bild hoch.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((item) => (
                <Card key={item.id} className="overflow-hidden">
                  <button
                    type="button"
                    className="relative aspect-square w-full bg-muted/30 block cursor-zoom-in"
                    onClick={() => item.public_url && setPreviewItem(item)}
                    aria-label="Vorschau"
                  >
                    {item.media_kind === 'video' ? (
                      <span className="flex h-full w-full items-center justify-center overflow-hidden">
                        {item.thumbnail_url ? (
                          <img src={item.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <Film className="h-8 w-8 text-muted-foreground" />
                        )}
                        {item.processing_status && (
                          <span
                            className={cn(
                              'absolute bottom-1 left-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
                              item.processing_status === 'ready'
                                ? 'bg-primary/20 text-primary'
                                : item.processing_status === 'failed'
                                  ? 'bg-destructive/20 text-destructive'
                                  : 'bg-foreground/20 text-foreground',
                            )}
                          >
                            {PROCESSING_LABELS[item.processing_status] ?? item.processing_status}
                          </span>
                        )}
                      </span>
                    ) : istPdf(item) ? (
                      <span className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
                        <FileText className="h-8 w-8" />
                        <span className="text-xs font-medium">PDF</span>
                      </span>
                    ) : item.public_url ? (
                      <img
                        /* Vorschaufassung statt Original: die Kacheln zogen sonst
                           die vollen Dateien — im Bestand im Schnitt 2,2 MB, die
                           groesste 11,6 MB. Der Rueckfall greift fuer Bilder, die
                           vor der serverseitigen Verkleinerung hochgeladen wurden. */
                        src={item.thumbnail_url || item.public_url}
                        alt={item.alt_text || item.original_filename}
                        className="h-full w-full object-cover transition-transform duration-200 hover:scale-[1.03]"
                        loading="lazy"
                      />
                    ) : null}
                    <AiDisclosureBadge value={item.ai_disclosure} />
                  </button>
                  <CardContent className="space-y-2 p-3">
                    <div className="truncate text-sm font-medium" title={item.original_filename}>
                      {item.title || item.original_filename}
                    </div>
                    <Select
                      value={String(item.folder_id ?? 0)}
                      onValueChange={(v) => moveTo(item, Number(v))}
                    >
                      <SelectTrigger size="sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Ohne Ordner</SelectItem>
                        {folders.map((f) => (
                          <SelectItem key={f.id} value={String(f.id)}>
                            {f.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <AiDisclosureSelect
                      value={item.ai_disclosure}
                      onChange={(v) => setDisclosure(item, v)}
                    />
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => copyUrl(assetUrl(item))}>
                        <Copy className="mr-2 h-4 w-4" /> URL
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        loading={deletingId === item.id}
                        onClick={() => remove(item)}
                        aria-label="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!previewItem} onOpenChange={(o) => { if (!o) setPreviewItem(null); }}>
        <DialogContent size="2xl">
          <DialogHeader>
            <DialogTitle className="truncate">
              {previewItem?.title || previewItem?.original_filename}
            </DialogTitle>
            <DialogDescription>
              {previewItem ? `${previewItem.content_type} · ${(previewItem.content_length / 1024).toFixed(0)} KB` : ''}
            </DialogDescription>
          </DialogHeader>
          {previewItem?.public_url && (
            <div className="flex max-h-[70vh] items-center justify-center overflow-auto rounded-md bg-muted/20">
              {istPdf(previewItem) ? (
                <iframe
                  src={previewItem.public_url}
                  title={previewItem.title || previewItem.original_filename}
                  className="h-[70vh] w-full"
                />
              ) : previewItem.media_kind === 'video' ? (
                <video
                  src={previewItem.public_url}
                  poster={previewItem.thumbnail_url || undefined}
                  controls
                  playsInline
                  className="max-h-[70vh] w-auto"
                />
              ) : (
                <img
                  src={previewItem.public_url}
                  alt={previewItem.alt_text || previewItem.original_filename}
                  className="max-h-[70vh] w-auto object-contain"
                />
              )}
            </div>
          )}
          {previewItem?.media_kind === 'video' && previewItem.processing_status !== 'ready' && (
            <p className="text-sm text-muted-foreground">
              Status: {PROCESSING_LABELS[previewItem.processing_status ?? ''] ?? previewItem.processing_status}.
              Die HLS-Streaming-Version steht erst nach Abschluss der Verarbeitung zur Verfügung.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => copyUrl(previewItem ? assetUrl(previewItem) : null)}>
              <Copy className="mr-2 h-4 w-4" /> URL kopieren
            </Button>
            {previewItem?.public_url && (
              <Button asChild type="button" variant="ghost" size="sm">
                <a href={previewItem.public_url} target="_blank" rel="noopener noreferrer">
                  In neuem Tab öffnen
                </a>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AiDisclosureDialog
        open={!!pendingDisclosure}
        filename={pendingDisclosure?.title || pendingDisclosure?.original_filename}
        onChoose={(v) => void resolvePendingDisclosure(v)}
      />
    </div>
  );
};

export default CmsMediaLibrary;
